import type { StateCreator } from 'zustand';
import type { ReviewHistory } from '../../types';
import type { StoreState } from '../useStore';
import * as dataApi from '../../services/dataApi';
import { normalizeReviewHistory } from '../normalizers';
import {
  queueOfflineReview,
  getOfflineQueue,
  clearOfflineQueue,
  getOfflineCardUpdateQueue,
  clearOfflineCardUpdateQueue,
  queueOfflineCardUpdate,
  getOfflineSettingsUpdateQueue,
  clearOfflineSettingsUpdateQueue,
  queueOfflineSettingsUpdate,
} from '../../utils/offlineSync';
import { reportError, trackBreadcrumb } from '@/services/errorTracking';
import type { PersistenceReceipt } from '@/utils/offlineSync';

export interface ReviewSlice {
  reviewHistory: ReviewHistory[];
  setReviewHistory: (history: ReviewHistory[]) => void;
  addReviewHistory: (review: Omit<ReviewHistory, 'id'>) => Promise<PersistenceReceipt>;
  loadFullReviewHistory: () => Promise<void>;
  syncOfflineReviews: () => Promise<void>;
  removeLastReviewForCard: (cardId: string) => void;
}

export let _isSyncing = false; // mutex for syncOfflineReviews

export const createReviewSlice: StateCreator<StoreState, [], [], ReviewSlice> = (set) => ({
  reviewHistory: [],
  setReviewHistory: (reviewHistory) => set({ reviewHistory }),

  addReviewHistory: async (review) => {
    // Optimistic local update
    const reviewedAt = review.reviewedAt || new Date().toISOString();
    set((state) => ({
      reviewHistory: [...state.reviewHistory, { ...review, id: crypto.randomUUID(), reviewedAt }],
    }));

    try {
      await dataApi.addReviewHistory({
        cardId: review.cardId,
        rating: review.rating,
        reviewDurationMs: review.reviewDurationMs,
        reviewedAt,
      });
      return 'persisted';
    } catch (err) {
      if (
        !navigator.onLine ||
        err instanceof TypeError ||
        (err instanceof Error && err.message.includes('Failed to fetch'))
      ) {
        queueOfflineReview({
          cardId: review.cardId,
          rating: review.rating,
          reviewDurationMs: review.reviewDurationMs,
          reviewedAt,
        });
        // Expected success-path when offline; not reportError (Vidi-02m).
        trackBreadcrumb('offline', 'Review queued for offline sync');
        return 'queued';
      } else {
        reportError(err, { operation: 'addReviewHistory' });
        throw err;
      }
    }
  },

  loadFullReviewHistory: async () => {
    try {
      const raw = await dataApi.fetchReviewHistory();
      const history = (Array.isArray(raw) ? raw : []).map((r) =>
        normalizeReviewHistory(r as Record<string, unknown>),
      );
      set({ reviewHistory: history });
    } catch (err) {
      reportError(err, { operation: 'loadFullReviewHistory' });
    }
  },

  syncOfflineReviews: async () => {
    // Mutex: prevent concurrent sync runs from replaying durable offline
    // mutations twice. The name is historical; this now syncs the full review
    // commit bundle: card SRS updates, review history, and settings deltas.
    if (_isSyncing) return;
    _isSyncing = true;

    try {
      const cardUpdates = getOfflineCardUpdateQueue();
      const reviews = getOfflineQueue();
      const settingsUpdates = getOfflineSettingsUpdateQueue();
      if (cardUpdates.length === 0 && reviews.length === 0 && settingsUpdates.length === 0) return;

      // Clear queues first to prevent re-reads by concurrent online triggers.
      // Failed entries are re-queued below in their original order.
      clearOfflineCardUpdateQueue();
      clearOfflineQueue();
      clearOfflineSettingsUpdateQueue();

      const failedCardUpdates: typeof cardUpdates = [];
      for (const update of cardUpdates) {
        try {
          await dataApi.updateCard(update.cardId, update.updates);
        } catch {
          failedCardUpdates.push(update);
        }
      }

      const failedReviews: typeof reviews = [];
      for (const review of reviews) {
        try {
          await dataApi.addReviewHistory({
            cardId: review.cardId,
            rating: review.rating,
            reviewDurationMs: review.reviewDurationMs,
            reviewedAt: review.reviewedAt ?? review.queuedAt,
          });
        } catch {
          failedReviews.push(review);
        }
      }

      const failedSettingsUpdates: typeof settingsUpdates = [];
      for (const update of settingsUpdates) {
        try {
          await dataApi.updateUserSettings(update.updates);
        } catch {
          failedSettingsUpdates.push(update);
        }
      }

      if (failedCardUpdates.length > 0) {
        for (const update of failedCardUpdates) {
          queueOfflineCardUpdate({
            cardId: update.cardId,
            updates: update.updates,
          });
        }
      }

      if (failedReviews.length > 0) {
        for (const r of failedReviews) {
          queueOfflineReview({
            cardId: r.cardId,
            rating: r.rating,
            reviewDurationMs: r.reviewDurationMs,
            reviewedAt: r.reviewedAt ?? r.queuedAt,
          });
        }
      }

      if (failedSettingsUpdates.length > 0) {
        for (const update of failedSettingsUpdates) {
          queueOfflineSettingsUpdate({ updates: update.updates });
        }
      }
    } finally {
      _isSyncing = false;
    }
  },

  removeLastReviewForCard: (cardId) => {
    set((state) => {
      const idx = [...state.reviewHistory].reverse().findIndex((r) => r.cardId === cardId);
      if (idx === -1) return state;
      const realIdx = state.reviewHistory.length - 1 - idx;
      const updated = [...state.reviewHistory];
      updated.splice(realIdx, 1);
      return { reviewHistory: updated };
    });
    dataApi
      .deleteLastReviewForCard(cardId)
      .catch((err) => reportError(err, { operation: 'removeLastReviewForCard' }));
  },
});
